import { useState, useEffect } from 'react';
import { Download, FileText, Video, Image, File, ExternalLink } from 'lucide-react';

const FILE_ICONS = {
  PDF: FileText,
  VIDEO: Video,
  IMAGE: Image,
  OTHER: File
};

const FILE_COLORS = {
  PDF: 'bg-red-100 text-red-600',
  VIDEO: 'bg-purple-100 text-purple-600',
  IMAGE: 'bg-green-100 text-green-600',
  OTHER: 'bg-gray-100 text-gray-600'
};

// Convert Google Drive link to direct download URL (bypasses verification)
function convertGoogleDriveLink(url) {
  if (!url) return url;
  const match = url.match(/drive\.google\.com\/(?:file\/d\/|open\?id=)([a-zA-Z0-9_-]+)/);
  if (match) {
    return `https://drive.google.com/uc?export=download&id=${match[1]}`;
  }
  return url;
}

export default function Downloads() {
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDownloads();
  }, []);

  const loadDownloads = async () => {
    try {
      const res = await fetch('/api/downloads');
      const data = await res.json();
      setDownloads(data.downloads || []);
    } catch (err) {
      console.error('Error loading downloads:', err);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (type) => {
    const Icon = FILE_ICONS[type] || File;
    return <Icon className="w-6 h-6" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-500">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-black text-gray-900 mb-2">📥 Descargas</h1>
        <p className="text-gray-500">Manuales, catálogos y recursos para tu moto</p>
      </div>

      {downloads.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm text-center">
          <Download className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-xl font-semibold text-gray-500">No hay descargas disponibles</p>
          <p className="text-gray-400 mt-2">El administrador agregará contenido pronto</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {downloads.map((item) => {
            const Icon = getIcon(item.file_type);
            const colorClass = FILE_COLORS[item.file_type] || FILE_COLORS.OTHER;
            
            return (
              <a
                key={item.id}
                href={convertGoogleDriveLink(item.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition border border-gray-100 flex items-center gap-4 group"
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${colorClass}`}>
                  {Icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-lg text-gray-900 group-hover:text-primary transition truncate">
                    {item.title}
                  </h3>
                  {item.description && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{item.description}</p>
                  )}
                  <span className="text-xs text-gray-400 mt-2 inline-block capitalize">
                    {item.file_type === 'OTHER' ? 'Archivo' : item.file_type.toLowerCase()}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-primary opacity-0 group-hover:opacity-100 transition">
                  <span className="text-sm font-medium">Descargar</span>
                  <ExternalLink className="w-5 h-5" />
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
